/**
 * The Bio Link settings (owner decisions 2026-10-20; design smartlink-editor e4) and the rules of the
 * Bio Link auto add: when the artist adds a song to the Catalog, the song's "Catalog links" block
 * (widget type `presave`) goes on the account's Bio Link (`AccountModel.mainSmartLinkId`) and is live
 * at once. No heavy imports: the API, the web app and the MCP tools read this file.
 *
 * API: `GET /accounts/:accountId/bio-link-settings` → `BioLinkSettingsResponse`;
 * `PUT /accounts/:accountId/bio-link-settings` with a partial `BioLinkSettings` → the same answer.
 * The add-song routes (`POST .../catalog/unreleased`, `.../catalog/claim`, `.../catalog/released`)
 * take `addToBioLink?: boolean` (absent = `autoAddSongs`) and answer `bioLink: BioLinkAutoAddResult`.
 */

/** Where an auto-added block goes on the Bio Link. */
export type BioLinkAutoAddPosition = 'afterProfile' | 'end';

export const BIO_LINK_AUTO_ADD_POSITIONS: readonly BioLinkAutoAddPosition[] = ['afterProfile', 'end'];

/** How many auto-added song blocks stay shown. Null = all. A block over the count is hidden, never deleted. */
export type BioLinkKeepSongs = 1 | 3 | 5 | null;

export const BIO_LINK_KEEP_SONGS_OPTIONS: readonly BioLinkKeepSongs[] = [1, 3, 5, null];

export interface BioLinkSettings {
  /** "Add new songs automatically". On by default (owner decision 2026-10-20). */
  autoAddSongs: boolean;
  /** "Under your profile" (after the Profile and Social links blocks at the top) or "At the end". */
  position: BioLinkAutoAddPosition;
  /** "Older songs": keep the newest N auto-added blocks shown; null = keep all. */
  keepSongs: BioLinkKeepSongs;
}

export const DEFAULT_BIO_LINK_SETTINGS: BioLinkSettings = {
  autoAddSongs: true,
  position: 'afterProfile',
  keepSongs: null,
};

export interface BioLinkSettingsResponse {
  settings: BioLinkSettings;
  /** The account's Bio Link page, or null when the account has none. */
  bioLinkSmartLinkId: number | null;
}

/** What the auto add did for one new song (the add-song Done step, design e2). */
export interface BioLinkAutoAddResult {
  /** True when the block is on the Bio Link now (new, or the song had one already). */
  added: boolean;
  /**
   * Why nothing new was added. Null when the block is new. `alreadyOnPage` comes with `added: true`
   * (the song had a block on the page already).
   */
  skippedReason: 'notAsked' | 'noBioLink' | 'demo' | 'alreadyOnPage' | 'failed' | null;
  /** The Bio Link page. Null with no Bio Link. */
  smartLinkId: number | null;
  /** The uuid of the block (the same uuid on the live version and the draft). */
  widgetUuid: string | null;
  /** True when the Bio Link is published, so fans see the block now. */
  live: boolean;
}

/** The widget types that count as "your profile" at the top of a Bio Link. */
const PROFILE_BLOCK_TYPES = new Set(['profile', 'socialLinks']);

/**
 * The position of a new auto-added block on one version.
 *
 * `widgets` are the blocks of the version (any order). `afterProfile`: after the run of Profile and
 * Social links blocks at the top of the page (0 when the page does not start with one); `end`: after
 * the last block.
 */
export function bioLinkInsertPosition(
  widgets: readonly { type: string; position: number }[],
  position: BioLinkAutoAddPosition
): number {
  const sorted = [...widgets].sort((a, b) => a.position - b.position);
  if (position === 'end') {
    return sorted.length === 0 ? 0 : (sorted[sorted.length - 1]?.position ?? -1) + 1;
  }
  let insertAt = 0;
  for (const widget of sorted) {
    if (!PROFILE_BLOCK_TYPES.has(widget.type)) {
      break;
    }
    insertAt = widget.position + 1;
  }
  return insertAt;
}

/**
 * The uuids of the auto-added blocks that the "keep the newest N" rule hides now.
 *
 * Only shown, auto-added blocks count (`autoAddedAt` set, `hidden` false); the newest `keepSongs`
 * stay. A block that the artist added by hand, or a block that is already hidden, never counts.
 */
export function bioLinkBlocksToHide(
  blocks: readonly { uuid: string; hidden: boolean; autoAddedAt: string | null }[],
  keepSongs: BioLinkKeepSongs
): string[] {
  if (keepSongs === null) {
    return [];
  }
  const shownAuto = blocks
    .filter((block) => !block.hidden && block.autoAddedAt !== null)
    .sort((a, b) => (b.autoAddedAt ?? '').localeCompare(a.autoAddedAt ?? ''));
  return shownAuto.slice(keepSongs).map((block) => block.uuid);
}

/** Parses a stored or requested value; anything unknown gives the default. */
export function parseBioLinkSettings(value: Partial<Record<keyof BioLinkSettings, unknown>> | null): BioLinkSettings {
  const autoAddSongs =
    typeof value?.autoAddSongs === 'boolean' ? value.autoAddSongs : DEFAULT_BIO_LINK_SETTINGS.autoAddSongs;
  const position = BIO_LINK_AUTO_ADD_POSITIONS.includes(value?.position as BioLinkAutoAddPosition)
    ? (value?.position as BioLinkAutoAddPosition)
    : DEFAULT_BIO_LINK_SETTINGS.position;
  const keepSongs = BIO_LINK_KEEP_SONGS_OPTIONS.includes(value?.keepSongs as BioLinkKeepSongs)
    ? (value?.keepSongs as BioLinkKeepSongs)
    : DEFAULT_BIO_LINK_SETTINGS.keepSongs;
  return { autoAddSongs, position, keepSongs };
}
